import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getServiceComments } from '../redux/actions/services';
import {
  SERVICE_CHAT_CHANNEL,
  SERVICE_CHAT_MESSAGE_SENT,
  SERVICE_CHAT_STORAGE_KEY,
  SERVICE_CHAT_TAB_ID,
  broadcastServiceChatMessageSent
} from '../views/services/serviceDetailSubComponents/rightSection/chatboxUtils';

/**
 * Keeps work order chat in sync across browser tabs.
 * When a message is sent in one tab, other tabs viewing the same
 * service request immediately refetch newer comments.
 */
export const useServiceChatCrossTabSync = ({ serviceId, getLastCommentId }) => {
  const dispatch = useDispatch();
  const channelRef = useRef(null);
  const getLastCommentIdRef = useRef(getLastCommentId);
  getLastCommentIdRef.current = getLastCommentId;

  useEffect(() => {
    if (!serviceId) return undefined;

    const refetchComments = (incomingServiceId, senderId) => {
      if (senderId === SERVICE_CHAT_TAB_ID) return;
      if (String(incomingServiceId) !== String(serviceId)) return;

      dispatch(
        getServiceComments({
          id: serviceId,
          commentId: getLastCommentIdRef.current?.() || 0
        })
      );
    };

    let channel;
    if (typeof window.BroadcastChannel === 'function') {
      channel = new window.BroadcastChannel(SERVICE_CHAT_CHANNEL);
      channelRef.current = channel;
      channel.onmessage = (event) => {
        const { type, serviceId: incomingServiceId, senderId } = event.data || {};
        if (type !== SERVICE_CHAT_MESSAGE_SENT) return;
        refetchComments(incomingServiceId, senderId);
      };
    }

    const onStorage = (event) => {
      if (event.key !== SERVICE_CHAT_STORAGE_KEY || !event.newValue) return;
      try {
        const data = JSON.parse(event.newValue);
        if (data?.type !== SERVICE_CHAT_MESSAGE_SENT) return;
        refetchComments(data.serviceId, data.senderId);
      } catch (_) {
        // ignore malformed payloads
      }
    };

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      if (channel) {
        channel.close();
      }
      channelRef.current = null;
    };
  }, [dispatch, serviceId]);

  const notifyOtherTabs = useCallback(() => {
    broadcastServiceChatMessageSent(serviceId, channelRef.current);
  }, [serviceId]);

  return { notifyOtherTabs };
};
